import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import performanceMonitor from "@/lib/performanceMonitor";
import useContentCacheInvalidator from "@/hooks/useContentCacheInvalidator";

import { Suspense, lazy } from "react";
const HomePage = lazy(() => import("@/pages/HomePage"));
const ContentDetailPage = lazy(() => import("@/pages/ContentDetailPage"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const UserProfile = lazy(() => import("@/pages/UserProfile"));
const ContentPage = lazy(() => import("@/pages/ContentPage"));
const UserDetailPage = lazy(() => import("@/pages/UserDetailPage"));
const RafflePage = lazy(() => import("@/pages/RafflePage"));
import Header from "@/components/ui/header";
import { ContentCacheProvider } from "@/contexts/ContentCacheContext";

// Search context to share searchQuery between Header and HomePage
export const SearchContext = React.createContext({
  searchQuery: "",
  setSearchQuery: () => {},
});

const DynamicPromotionPage = lazy(() => import("@/pages/DynamicPromotionPage"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);

function App() {
  const [searchQuery, setSearchQuery] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Monitorear cambios en el caché de contenido del servidor
  useContentCacheInvalidator();

  // Inicializar monitoreo de performance
  useEffect(() => {
    performanceMonitor.init();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      // If the server explicitly rejects the token (401/403), remove it.
      // For network errors (timeout, unreachable) we keep the token so the app
      // doesn't immediately log out the user while the backend is temporarily down.
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        localStorage.removeItem("token");
        delete axios.defaults.headers.common["Authorization"];
      } else {
        // Network error / timeout / no response
        // Notify the user (toast) but preserve the stored token.
        try {
          toast.error("No se pudo conectar al backend. Verifica conexión o la URL del backend.");
        } catch {}
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    toast.success("Sesión cerrada exitosamente");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <SearchContext.Provider value={{ searchQuery, setSearchQuery }}>
        <ContentCacheProvider>
          <div className="App">
            <BrowserRouter>
              <Header />
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"><div className="text-white text-xl">Cargando página...</div></div>}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/raffle" element={<RafflePage />} />
                  <Route path="/contenido" element={<ContentPage />} />
                  <Route path="/content/:id" element={<ContentDetailPage />} />
                  <Route path="/promotion/:id" element={<DynamicPromotionPage />} />
                  <Route
                    path="/profile"
                    element={
                      user ? <UserProfile /> : <Navigate to="/" replace />
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      user?.role === "admin" ? (
                        <AdminPanel />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/admin/user/:userId"
                    element={
                      user?.role === "admin" ? (
                        <UserDetailPage />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <Toaster position="top-right" />
          </div>
        </ContentCacheProvider>
      </SearchContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;